// Design tokens for the room: colors, fonts, radius, shadows, type.
//
// Since 2026-10-26 the brand values come from the Bison brand repo
// (Bison-Office/Bison-brand, tokens/tokens.json at its release tag) through
// ./brand.generated.ts, which scripts/sync-brand.mjs writes; never type a
// brand value here. The brand repo is initiated from the public website, so
// the room and bisoncommerce.com read as one brand. What this file adds on
// top is room-only: the names the code uses, the extended palette, the chart
// sequence, the portal chrome.
//
// Imported by the MUI theme (theme.ts) and any non-MUI surface (charts).

import { brand } from "./brand.generated";

const px = (value: string) => parseFloat(value);

export const colors = {
    // Core brand (brand color: navy, navy-light, orange, orange-bright)
    navy: brand.color.navy,
    navyLight: brand.color["navy-light"], // closing-CTA navy; never flush against plain navy
    brandOrange: brand.color.orange,
    orangeBright: brand.color["orange-bright"], // primary-button hover only

    // Text (brand color: body, emphasis, muted)
    darkGray: brand.color.body, // body copy
    emphasis: brand.color.emphasis, // strong text on light surfaces
    mediumGray: brand.color.muted, // muted / captions

    // Surfaces (brand color: bg, surface, line, line-soft). The room's page
    // ground is white since 2026-10-26 (theme.ts); `background` is the PDF
    // viewers' frame.
    background: brand.color.bg,
    lightGray: brand.color.surface, // "surface": light bands, chips, tile fills
    line: brand.color.line, // 1px borders on cards, tables, inputs
    lineSoft: brand.color["line-soft"], // hairline dividers inside a card
    white: brand.color.white,

    // Functional link blue is retired on the deal room (2026-09-03): links now
    // read the site's way (navy, orange underline). Kept for charts/legacy.
    linkBlue: "#1A3DF7",
} as const;

// Corner radius, in px. The site caps every radius at 10px; so do we.
// In `sx`, MUI multiplies a bare number by theme.shape.borderRadius (8px),
// so `borderRadius: 2` is 16px and OVER the cap — always use these tokens or
// a px string for anything larger than `borderRadius: 1`.
export const radius = {
    sm: px(brand.radius.sm), // small controls
    md: px(brand.radius.md), // buttons, inputs
    lg: px(brand.radius.lg), // cards, tiles, embeds: the cap
    full: px(brand.radius.full), // chips and pagination only: the one exception to the cap
} as const;

// Navy-tinted shadows (brand shadow sm to xl).
export const shadows = brand.shadow;

// The site's clickable-card treatment (Insights `PostCard`): 1px line border,
// white, shadow-sm; on hover the border deepens to navy/20 and the shadow
// lifts to md. Nothing else moves. Spread into `sx` on any card that is a link.
export const clickableCard = {
    border: 1,
    borderColor: colors.line,
    bgcolor: colors.white,
    boxShadow: shadows.sm,
    transition: "border-color 150ms ease, box-shadow 150ms ease",
    "&:hover": { borderColor: "rgba(0, 31, 55, 0.2)", boxShadow: shadows.md },
} as const;

// Chart palette — the design system's CHART_COLORS sequence, in order
// (assets/brand/brand-guide.md § Chart Color Sequence). A single series is
// chartPalette[0], Blue; a two-series comparison is Blue + Green; brand orange
// #F03A12 is never a data series and (Bill, 2026-08-28) never an emphasis bar
// either. Navy is for chart titles, not bars. Until 2026-09-08 this array was
// navy-first, copied from a stale line in the brand guide, and every chart on
// the room drew navy.
export const chartPalette = [
    "#0070C0", // 1. Blue
    "#059669", // 2. Green
    "#E8910C", // 3. Orange (extended palette, not brand orange)
    "#7C3AED", // 4. Purple
    "#DC2626", // 5. Red
    "#003865", // 6. Blue Dark
    "#EC4899", // 7. Pink
    "#92400E", // 8. Brown
    "#69BFFF", // 9. Blue Light
    "#34D399", // 10. Green Light
    "#FFC000", // 11. Light Gold
    "#A78BFA", // 12. Purple Light
] as const;

// Semantic colors for callouts. NEVER green — positive outcomes go in prose
// or KPI deltas, not in callouts. Per brand guide.
export const semantic = {
    info: { bg: "#E8F0F8", border: "#1E5C8A", text: "#0A3556" },
    warning: { bg: "#FDF4E3", border: "#C18A1F", text: "#6B4910" },
    danger: { bg: "#FBEAE5", border: "#C0392B", text: "#5A1A12" },
} as const;

// Extended palette from assets/brand/brand-guide.md (the `--bc-*` block the
// HTML reports use). The site's @theme does not carry these; the deal room
// reaches for them where a page needs more than navy / orange / gray to read
// (2026-09-05, Bill: the Onboarding Checklist's done / current / upcoming
// states and document pills). Green marks something finished, blue marks the
// thing in hand or a document, gold and purple are spare. Orange stays the
// scarce accent it is on the site.
export const palette = {
    blue: "#0070C0",
    blueLight: "#69BFFF",
    blueDark: "#003865",
    green: "#059669",
    greenLight: "#34D399",
    gold: "#FFC000",
    orangeExt: "#E8910C",
    purple: "#7C3AED",
    purpleLight: "#A78BFA",
    // Tints (bg + border) for the three states the checklist uses.
    successBg: "#ECFDF5",
    successBorder: "#A7F3D0",
    infoBg: "#EFF6FF",
    infoBorder: "#BFDBFE",
    warningBg: "#FFFBEB",
    warningBorder: "#FDE68A",
    // Owner pills on the checklist (2026-09-24, Bill): Bison reads on the
    // info tint, the supplier on purple, a shared step on gold.
    purpleBg: "#F5F3FF",
    purpleBorder: "#DDD6FE",
    purpleDark: "#5B21B6",
    goldDark: "#6B4910",
} as const;

// Inter at 300–800, same as the site (`app/fonts.ts`). Loaded in index.html.
export const fonts = {
    body: brand.type.sans.stack,
} as const;

// The brand type scale (brand `type.scale`, which carries the site's style
// guide). The desktop sizes and leading come from the brand; the step down
// below `sm` is the site's (`text-4xl sm:text-5xl` for the page title,
// `text-3xl sm:text-4xl` for the section title), which the brand tokens do
// not carry. Spread into `sx`; the theme's h1 / h2 read the same values.
const scale = brand.type.scale;
export const typeScale = {
    // Page title: 36px, 48px from sm. Every page H1 in the room.
    pageTitle: { fontSize: { xs: "2.25rem", sm: scale["page-title"].size }, lineHeight: Number(scale["page-title"].line) },
    // Section title: 30px, 36px from sm. Feature row titles, section heads.
    sectionTitle: {
        fontSize: { xs: "1.875rem", sm: scale["section-title"].size },
        lineHeight: Number(scale["section-title"].line),
    },
    // Lead: 18px / 1.625. Intros under an H1 and feature row bodies.
    lead: { fontSize: scale.lead.size, lineHeight: Number(scale.lead.line) },
} as const;

// admin-ui neutral scales (mirrors @bisoncommerce/shared-ui, via content-
// optimization-web / pricing-universe-web). The shared left-drawer chrome reads
// from these so the deal room's nav is pixel-identical to the other portal
// surfaces. Solid greys for surfaces/borders, opacity-on-ink greys for text/
// icons. Distinct from the navy/orange report palette above (which still drives
// the page *content*).
export const blackSolid = {
    100: "#0A0819",
    90: "#232130",
    80: "#3B3947",
    70: "#54525E",
    60: "#6C6B75",
    50: "#84838C",
    40: "#9D9CA3",
    30: "#B5B5BA",
    20: "#CECED1",
    10: "#E6E6E8",
    5: "#F3F3F3",
    ["025"]: "#F8F8F8",
    0: "#FFFFFF",
} as const;

export const blackOpacity = {
    100: "#0A0819",
    90: "#0A0819E5",
    80: "#0A0819CC",
    70: "#0A0819B3",
    60: "#0A081999",
    50: "#0A081980",
    40: "#0A081966",
    30: "#0A08194D",
    20: "#0A081933",
    10: "#0A08191A",
    0: "#0A081900",
} as const;

// Chrome tokens (left drawer) — matches content-optimization-web's `tokens`.
export const chrome = {
    ink: blackSolid[100], // active nav label / primary chrome text
    inkBody: blackOpacity[80], // idle nav label
    iconPrimary: blackOpacity[60], // collapse glyph + nav icons
    borderPrimary: blackSolid[10], // drawer divider / right border
    surfaceActive: blackSolid[10], // active nav pill fill
    surfaceHover: blackSolid[5], // nav hover
} as const;

export const spacing = {
    // MUI's default 8px grid is fine; this is here for non-MUI surfaces only.
    unit: 8,
} as const;
